import { useState, useEffect } from "react";
import { doc, getDoc, setDoc, increment } from "firebase/firestore";
import { db } from "./firebase";

/**
 * Community poll widget displayed in the sidebar.
 * Reads/writes votes to a Firestore "polls" document and persists vote/collapse
 * state to localStorage. Disabled by default — set isVisible to true to activate.
 *
 * @returns {JSX.Element|null} The poll card, a collapsed "Show Poll" button, or null when disabled.
 */
const Poll = () => {
  const isVisible = false; // Toggle this to true when new question

  const [hasVoted, setHasVoted] = useState(false);
  const [results, setResults] = useState(null);
  const [totalVotes, setTotalVotes] = useState(0);
  const [isCollapsed, setIsCollapsed] = useState(false); // Tracks if it's minimized

  const pollId = "poll_question_3";
  const pollQuestion = "Should we change Craft Mode so it shows the name of a Craft and you have to guess the Character, instead of having to guess the Craft by description?";
  const options = [
    { id: "yes", label: "Yes, that would be more fun" },
    { id: "no", label: "No, I like the current version" },
    { id: "maybe", label: "I don't mind either way"},
  ];

  useEffect(() => {
    if (!isVisible) return;

    // 1. Check if they minimized this specific poll
    if (localStorage.getItem(`collapsed_${pollId}`) === "true") {
      setIsCollapsed(true);
    }

    // 2. Check if they voted
    if (localStorage.getItem(`voted_${pollId}`)) {
      setHasVoted(true);
      fetchResults();
    }
  }, [isVisible]);

  /** Fetches current vote tallies from Firestore and updates local state. */
  const fetchResults = async () => {
    const docRef = doc(db, "polls", pollId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      setResults(data);
      const total = options.reduce((sum, opt) => sum + (data[opt.id] || 0), 0);
      setTotalVotes(total === 0 ? 1 : total);
    }
  };

  /** Records the user's vote in Firestore and updates local results. */
  const handleVote = async (choiceId) => {
    setHasVoted(true);
    const docRef = doc(db, "polls", pollId);
    await setDoc(
      docRef,
      {
        [choiceId]: increment(1),
      },
      { merge: true }
    );

    localStorage.setItem(`voted_${pollId}`, "true");
    fetchResults();
  };

  /** Toggles the poll between collapsed (minimized) and expanded states. */
  const togglePoll = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    localStorage.setItem(`collapsed_${pollId}`, newState.toString());
  };

  if (!isVisible) return null;

  // If it's collapsed, just show a small button!

  if (isCollapsed) {
    return (
      <div className="poll-collapsed-wrapper">
        <button
          onClick={togglePoll}
          className="poll-show-btn"
        >
          📊 Show Poll
        </button>
      </div>
    );
  }

  // Otherwise, show the full poll card
  return (
    <div className="poll-container">
      {/* Changed from "X" to a Collapse icon/text */}
      <button
        onClick={togglePoll}
        className="poll-hide-btn"
        title="Minimize Poll"
      >
        Hide
      </button>

      <h3 className="poll-title">📊 Quick Poll</h3>
      <p className="poll-question-text">{pollQuestion}</p>

      {!hasVoted ? (
        <div className="poll-options-list">
          {options.map((opt) => (
            <button
              key={opt.id}
              onClick={() => handleVote(opt.id)}
              className="poll-option-btn"
            >
              {opt.label}
            </button>
          ))}
        </div>
      ) : (
        <div className="poll-results-list">
          {results ? (
            options.map((opt) => {
              const votes = results[opt.id] || 0;
              const percentage = Math.round((votes / totalVotes) * 100);

              return (
                <div key={opt.id} className="poll-result-item">
                  <div className="poll-result-info">
                    <span>{opt.label}</span>
                    <span>
                      {votes} ({percentage}%)
                    </span>
                  </div>
                  <div className="poll-bar-track">
                    <div
                      className="poll-bar-fill"
                      style={{
                        width: `${percentage}%`,
                      }}
                    ></div>
                  </div>
                </div>
              );
            })
          ) : (
            <p>Loading results...</p>
          )}
        </div>
      )}
    </div>
  );
};

export default Poll;
